import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Camera, Scan, CheckCircle, XCircle } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useConferirPorCodigo } from '../hooks';

interface Props {
  open: boolean;
  inventoryId: string;
  onClose: () => void;
}

interface BipEntry {
  code: string;
  ok: boolean;
  message: string;
  ts: string;
}

/**
 * Modo conferência rápida — leitor USB (input focado) + câmera QR via
 * @zxing/library. Feedback visual + som (WebAudio API — sem lib).
 */
export function InventoryQuickCheckModal({ open, inventoryId, onClose }: Props) {
  const conferir = useConferirPorCodigo(inventoryId);
  const [code, setCode] = useState('');
  const [history, setHistory] = useState<BipEntry[]>([]);
  const [cameraOn, setCameraOn] = useState(false);
  const [flash, setFlash] = useState<'ok' | 'err' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else stopCamera();
  }, [open]);

  useEffect(() => {
    if (!cameraOn || !videoRef.current) return;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;
    reader.decodeFromVideoDevice(null, videoRef.current, (result) => {
      if (result) {
        const text = result.getText();
        handleBip(text);
      }
    }).catch(() => {
      setCameraOn(false);
      pushBip('', false, 'Falha ao acessar câmera');
    });
    return () => { reader.reset(); };
  }, [cameraOn]);

  function stopCamera() {
    readerRef.current?.reset();
    readerRef.current = null;
    setCameraOn(false);
  }

  function playTone(ok: boolean) {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = ok ? 880 : 220;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.start(); osc.stop(ctx.currentTime + 0.15);
    } catch {
      /* silêncio absoluto — som é conveniência */
    }
  }

  function pushBip(code: string, ok: boolean, message: string) {
    const entry = { code, ok, message, ts: new Date().toLocaleTimeString('pt-BR') };
    setHistory((h) => [entry, ...h].slice(0, 10));
    setFlash(ok ? 'ok' : 'err');
    playTone(ok);
    setTimeout(() => setFlash(null), 800);
  }

  async function handleBip(rawCode: string) {
    const cleaned = rawCode.trim();
    if (!cleaned) return;
    setCode('');
    try {
      const item = await conferir.mutateAsync({ assetCode: cleaned });
      pushBip(cleaned, true, `Localizado: ${item.asset?.displayCode ?? item.asset?.assetCode ?? ''}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      pushBip(cleaned, false, typeof msg === 'string' ? msg : 'Falha na leitura');
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); handleBip(code); }
  }

  return (
    <Dialog open={open} onClose={() => { stopCamera(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={`w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl transition ${
          flash === 'ok' ? 'ring-4 ring-green-400' : flash === 'err' ? 'ring-4 ring-red-400' : ''
        }`}>
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Scan className="h-5 w-5" /> Modo conferência rápida
            </Dialog.Title>
            <button onClick={() => { stopCamera(); onClose(); }} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="mb-3 text-xs text-gray-500">
            Bipe (leitor USB) ou digite o código patrimonial e pressione Enter. Alternativa: câmera QR.
          </p>

          <div className="mb-3 flex items-center gap-2">
            <input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Código patrimonial ou número de série..."
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={() => cameraOn ? stopCamera() : setCameraOn(true)}
              className={`inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm ${
                cameraOn ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Camera className="h-4 w-4" /> {cameraOn ? 'Parar câmera' : 'Ligar câmera'}
            </button>
          </div>

          {cameraOn && (
            <video ref={videoRef} className="mb-3 w-full rounded-md border border-gray-200" style={{ maxHeight: 240 }} />
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-700">Últimas leituras</h4>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500">Aguardando bipes...</p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto">
                {history.map((h, i) => (
                  <li key={i} className={`flex items-start gap-2 rounded-md p-2 text-xs ${
                    h.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    {h.ok ? <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className={h.ok ? 'text-green-800' : 'text-red-800'}>
                        <span className="font-mono">{h.code || '(vazio)'}</span> — {h.message}
                      </p>
                      <p className="text-[10px] text-gray-500">{h.ts}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
